import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2, Key, Copy, Globe, RotateCw, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_META = {
  active:  { label: "Active",   class: "text-emerald-700 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-300" },
  blocked: { label: "Bloquée",  class: "text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-300" },
  revoked: { label: "Révoquée", class: "text-rose-700 bg-rose-50 border-rose-200 dark:bg-rose-950/40 dark:border-rose-900 dark:text-rose-300" },
  pending: { label: "En attente", class: "text-sky-700 bg-sky-50 border-sky-200 dark:bg-sky-950/40 dark:border-sky-900 dark:text-sky-300" },
};

export default function LicensesManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ email: "", name: "", notes: "" });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/licenses");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.email.includes("@")) return toast.error("Email valide requis.");
    try {
      await api.post("/admin/licenses", createForm);
      toast.success("Licence créée");
      setCreating(false);
      setCreateForm({ email: "", name: "", notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      const payload = {
        status: editing.status,
        email: editing.email,
        name: editing.name,
        notes: editing.notes,
        domain: editing.domain,
      };
      await api.patch(`/admin/licenses/${editing.id}`, payload);
      toast.success("Licence mise à jour");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const resetDomain = async (lic) => {
    if (!confirm("Réinitialiser le domaine ? L'utilisateur pourra ré-installer sur un nouveau domaine.")) return;
    try {
      await api.patch(`/admin/licenses/${lic.id}`, { domain: "" });
      toast.success("Domaine réinitialisé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const resendEmail = async (lic) => {
    if (!confirm(`Renvoyer la clé ${lic.key} à ${lic.email} ?`)) return;
    try {
      const { data } = await api.post(`/admin/licenses/${lic.id}/resend-email`);
      if (data?.sent) {
        toast.success(`Email envoyé à ${data.to}`);
      } else {
        toast.warning(`Email non envoyé : ${data?.reason || "SMTP non configuré"}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (lic) => {
    if (!confirm(`Supprimer définitivement la licence ${lic.key} ?`)) return;
    try {
      await api.delete(`/admin/licenses/${lic.id}`);
      toast.success("Licence supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copié");
  };

  return (
    <div className="space-y-6" data-testid="licenses-manager">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">// Commercial</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Licences</h1>
          <p className="text-sm text-muted-foreground mt-1">Gestion des clés de licence WiLink (verrouillage par domaine).</p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="create-license-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouvelle licence
        </Button>
      </div>

      <section className="border border-border bg-card rounded-sm overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-sm text-muted-foreground">Chargement…</p>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <Key className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">Aucune licence pour le moment.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-[0.1em] text-muted-foreground">
              <tr>
                <th className="text-left p-3 font-medium">Clé</th>
                <th className="text-left p-3 font-medium">Client</th>
                <th className="text-left p-3 font-medium">Domaine</th>
                <th className="text-left p-3 font-medium">Statut</th>
                <th className="text-left p-3 font-medium">Dernier check</th>
                <th className="text-right p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((lic) => {
                const meta = STATUS_META[lic.status] || STATUS_META.active;
                return (
                  <tr key={lic.id} className="hover:bg-accent/40" data-testid={`license-row-${lic.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-xs">{lic.key}</code>
                        <button onClick={() => copy(lic.key)} className="text-muted-foreground hover:text-foreground" data-testid={`copy-${lic.id}`}>
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                    <td className="p-3">
                      <p className="font-medium">{lic.name || "—"}</p>
                      <p className="text-xs text-muted-foreground">{lic.email}</p>
                    </td>
                    <td className="p-3">
                      {lic.domain ? (
                        <span className="inline-flex items-center gap-1 font-mono text-xs">
                          <Globe className="h-3 w-3 text-muted-foreground" /> {lic.domain}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">non verrouillé</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${meta.class}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {lic.last_check_at ? new Date(lic.last_check_at).toLocaleString("fr-FR") : "—"}
                      <p className="text-[10px]">{lic.check_count || 0} checks</p>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => resendEmail(lic)} title="Renvoyer la clé par email" className="p-1.5 hover:bg-accent rounded" data-testid={`resend-email-${lic.id}`}>
                          <Send className="h-4 w-4 text-muted-foreground" />
                        </button>
                        {lic.domain && (
                          <button onClick={() => resetDomain(lic)} title="Réinitialiser le domaine" className="p-1.5 hover:bg-accent rounded" data-testid={`reset-domain-${lic.id}`}>
                            <RotateCw className="h-4 w-4 text-muted-foreground" />
                          </button>
                        )}
                        <button onClick={() => setEditing({ ...lic })} className="p-1.5 hover:bg-accent rounded" data-testid={`edit-${lic.id}`}>
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                        </button>
                        <button onClick={() => remove(lic)} className="p-1.5 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded" data-testid={`delete-${lic.id}`}>
                          <Trash2 className="h-4 w-4 text-rose-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="license-create-dialog">
          <DialogHeader>
            <DialogTitle>Nouvelle licence</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4">
            <div>
              <Label htmlFor="lic-email">Email du client *</Label>
              <Input id="lic-email" type="email" required value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                data-testid="lic-create-email" />
            </div>
            <div>
              <Label htmlFor="lic-name">Nom (optionnel)</Label>
              <Input id="lic-name" value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                data-testid="lic-create-name" />
            </div>
            <div>
              <Label htmlFor="lic-notes">Notes internes</Label>
              <Textarea id="lic-notes" rows={3} value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                data-testid="lic-create-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreating(false)}>Annuler</Button>
              <Button type="submit" data-testid="lic-create-submit">Générer la clé</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="license-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifier la licence</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={submitEdit} className="space-y-4">
              <div className="bg-muted/40 rounded-md p-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Clé</p>
                <code className="text-sm font-mono font-bold">{editing.key}</code>
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="lic-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="blocked">Bloquée</SelectItem>
                    <SelectItem value="revoked">Révoquée</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editing.email || ""}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              </div>
              <div>
                <Label>Nom</Label>
                <Input value={editing.name || ""}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <Label>Domaine</Label>
                <Input value={editing.domain || ""}
                  onChange={(e) => setEditing({ ...editing, domain: e.target.value })}
                  placeholder="Vide = non verrouillé" />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={3} value={editing.notes || ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
                <Button type="submit" data-testid="lic-edit-submit">Enregistrer</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
